import { RefObject, useCallback, useState } from 'react';
import { html2canvas } from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

type Result = {
  /** Собирает и отдаёт файл; во время сборки повторный вызов игнорируется */
  downloadPdf: () => Promise<void>;
  /** Идёт сборка файла */
  isBuilding: boolean;
};

/** Поля листа, мм. Без них снимок упирается в край и читается как обрезанный. */
const PAGE_MARGIN_MM = 8;

/**
 * Кратность снимка к экранному размеру карточки.
 *
 * Снимок кладётся на лист шириной около 190 мм, поэтому кратности 1.5 хватает на плотность
 * заметно выше печатной: подписи осей и цифры в таблицах остаются резкими. Дальше кратность
 * растит только вес файла — на бумаге и на экране разницы уже не видно.
 */
const SNAPSHOT_SCALE = 1.5;

/**
 * Качество JPEG для снимков карточек.
 *
 * Снимки кладутся в файл сжатыми: без сжатия отчёт из десятка дашбордов весит десятки
 * мегабайт и не проходит почтой, а он затем и нужен, чтобы уйти в переписку. На 0.8
 * артефактов на подписях и линиях графиков не видно, а вес падает на порядок.
 */
const JPEG_QUALITY = 0.8;

/**
 * Подложка снимка.
 *
 * Нужна явно: у карточек скруглены углы, и без подложки те остаются прозрачными, а JPEG
 * прозрачность не хранит — углы вышли бы чёрными.
 */
const SNAPSHOT_BACKGROUND = '#ffffff';

/**
 * Сборка PDF с дашбордами страницы.
 *
 * Источник файла — отрисованные карточки, а не данные ответа: дашборд существует только
 * нарисованным, и рисовать его второй раз другими средствами значило бы разойтись с экраном
 * при первой же правке любого виджета. Поэтому кнопка не делает ни одного запроса.
 *
 * Карточки снимаются по одной и раскладываются по листам целиком: карточка, разорванная
 * между страницами, в отчёте нечитаема. Снимки сжимаются: отчёт уходит в переписку, а
 * несжатый весит десятки мегабайт и почтой не проходит.
 */
export const useDashboardsPdf = (containerRef: RefObject<HTMLElement | null>, fileName: string): Result => {
  const [isBuilding, setIsBuilding] = useState(false);

  const downloadPdf = useCallback(async () => {
    const container = containerRef.current;
    if (!container || isBuilding) return;

    setIsBuilding(true);
    try {
      const cards = Array.from(container.children).filter((node): node is HTMLElement => node instanceof HTMLElement);
      if (cards.length === 0) return;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - PAGE_MARGIN_MM * 2;
      const contentHeight = pageHeight - PAGE_MARGIN_MM * 2;

      let offsetY = PAGE_MARGIN_MM;
      let isFirstOnPage = true;

      for (const card of cards) {
        const canvas = await html2canvas(card, { scale: SNAPSHOT_SCALE, backgroundColor: SNAPSHOT_BACKGROUND });
        const imageHeight = (canvas.height / canvas.width) * contentWidth;
        // Карточка выше листа: ужимаем её до листа целиком, но не режем.
        const height = Math.min(imageHeight, contentHeight);
        const width = (height / imageHeight) * contentWidth;

        if (!isFirstOnPage && offsetY + height > pageHeight - PAGE_MARGIN_MM) {
          pdf.addPage();
          offsetY = PAGE_MARGIN_MM;
          isFirstOnPage = true;
        }

        pdf.addImage(canvas.toDataURL('image/jpeg', JPEG_QUALITY), 'JPEG', PAGE_MARGIN_MM, offsetY, width, height);
        offsetY += height + PAGE_MARGIN_MM;
        isFirstOnPage = false;
      }

      pdf.save(fileName);
    } finally {
      setIsBuilding(false);
    }
  }, [containerRef, fileName, isBuilding]);

  return { downloadPdf, isBuilding };
};
